import React from 'react';
import { Box, Stack, ToggleButton, ToggleButtonGroup, CircularProgress, Typography } from '@mui/material';

import TierCard from './TierCard';

// Billing-cycle toggle plus the tier grid. One-time/annual is shown first
// and is the default view (client decision, Aug 2026 — supersedes the
// wireframe's "monthly is the default"). The "this is a 12-payment
// pledge" notice sits right under the toggle so nobody misses it when
// they do switch to monthly.
export default function TierSelector({
  tiers,
  loading,
  error,
  billingCycle,
  onBillingCycleChange,
  selectedTierId,
  onSelectTier,
}) {
  if (loading) {
    return (
      <Stack alignItems="center" py={6}>
        <CircularProgress sx={{ color: 'var(--navy-500)' }} />
      </Stack>
    );
  }

  if (error) {
    return <Typography color="error">{error}</Typography>;
  }

  return (
    <Box>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="center"
        alignItems="center"
        spacing={2}
        sx={{ mb: 2 }}
      >
        <ToggleButtonGroup
          exclusive
          value={billingCycle}
          onChange={(_, next) => next && onBillingCycleChange(next)}
          sx={{
            '& .MuiToggleButton-root': {
              px: 3,
              py: 1,
              fontFamily: 'var(--font-body)',
              fontWeight: 700,
              fontSize: 12.5,
              letterSpacing: '0.14em',
              textTransform: 'uppercase',
              color: 'var(--navy-500)',
              borderColor: 'rgba(36,34,88,0.18)',
            },
            '& .Mui-selected': {
              background: 'var(--teal-500) !important',
              color: '#fff !important',
            },
          }}
        >
          <ToggleButton value="annual">One-time / annual</ToggleButton>
          <ToggleButton value="monthly">Monthly</ToggleButton>
        </ToggleButtonGroup>
      </Stack>

      {billingCycle === 'monthly' && (
        <Typography
          align="center"
          sx={{
            fontFamily: 'var(--font-body)',
            fontSize: 13.5,
            color: 'var(--navy-500)',
            opacity: 0.85,
            maxWidth: 640,
            mx: 'auto',
            mb: 2,
          }}
        >
          12 fixed monthly payments — this is a yearly commitment, not a subscription you can
          cancel anytime.
        </Typography>
      )}

      {/* Exact copy lock (client decision email) — about processing-order
          priority, not literal seat-picking. Shown regardless of billing
          cycle, since the deadline applies to signing up at all. */}
      <Typography
        align="center"
        sx={{
          fontFamily: 'var(--font-body)',
          fontWeight: 700,
          fontSize: 14,
          color: 'var(--teal-500)',
          maxWidth: 640,
          mx: 'auto',
          mb: 4,
        }}
      >
        Sign up by September 1st and you're in the first seating assignments.
      </Typography>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: `repeat(${Math.min(tiers.length, 4) || 1}, 1fr)` },
          gap: 2.5,
          maxWidth: 1120,
          mx: 'auto',
        }}
      >
        {tiers.map((tier) => (
          <TierCard
            key={tier.id}
            tier={tier}
            billingCycle={billingCycle}
            selected={tier.id === selectedTierId}
            onSelect={onSelectTier}
          />
        ))}
      </Box>
    </Box>
  );
}
